import * as React from 'react';
import { Icon } from './icons/Icon';

export interface ExpectedAmountHintProps {
  /** When false, the slot renders empty but still reserves vertical space so
   * the surrounding layout doesn't shift as warnings appear/disappear. */
  show: boolean;
  /** Bare prefix text before the bolded amount, e.g. "Expected:" or
   * "Expected VAT:". Already translated. */
  label: string;
  /** Bolded amount + currency (already formatted), e.g. "2 100,00 CZK". */
  amount: string;
  /** Optional second-line derivation rendered in muted grey, e.g.
   * "= 10 000 CZK × 21 %". When omitted the slot reserves single-line height
   * (matches the legacy single-line warning); when present the slot reserves
   * two lines. */
  formula?: string;
  /** When supplied, an arrow-up icon button is rendered next to the amount;
   * clicking it should push the hinted value back into the input above. */
  onApply?: () => void;
  /** aria-label / title for the apply button. Required when `onApply` is set. */
  applyLabel?: string;
}

/**
 * Reserved-height hint rendered below a numeric input to convey an
 * expected/computed value when the typed value disagrees. Shared between the
 * VAT summary editor (with formula derivation) and the invoice-lines editor
 * (single-line variant). Caller supplies already-translated strings — this
 * component is layout only.
 */
export const ExpectedAmountHint: React.FC<ExpectedAmountHintProps> = ({
  show,
  label,
  amount,
  formula,
  onApply,
  applyLabel
}) => (
  <div className={formula ? 'mt-xs min-h-8' : 'mt-xs h-4'} aria-hidden={!show || undefined}>
    {show && (
      <div className='flex flex-col items-end text-2xs leading-tight text-warn-text whitespace-nowrap'>
        <span className='flex items-center gap-xs'>
          <Icon name='triangle-exclamation' className='w-3 h-3 shrink-0' />
          <span>
            {label} <strong className='font-semibold'>{amount}</strong>
          </span>
          {onApply && (
            <button
              type='button'
              onClick={onApply}
              aria-label={applyLabel}
              title={applyLabel}
              className='ml-2xs rounded p-2xs transition-colors hover:bg-warn-bg'
            >
              <Icon name='arrow-up' className='w-3 h-3' />
            </button>
          )}
        </span>
        {formula && <span className='text-foreground-muted'>{formula}</span>}
      </div>
    )}
  </div>
);

ExpectedAmountHint.displayName = 'ExpectedAmountHint';
